import type { CSSProperties } from 'react';
import AvatarIcon from './AvatarIcon';

export interface PopularQuestion {
  id: string;
  label: string;
  icon: 'robot' | 'sparkles' | 'target' | 'rocket';
}

const QUESTIONS: PopularQuestion[] = [
  { id: 'q1', label: 'What is Zetrix Avatar?', icon: 'robot' },
  { id: 'q2', label: 'How do I create an avatar?', icon: 'sparkles' },
  { id: 'q3', label: 'What can my avatar do?', icon: 'target' },
  { id: 'q4', label: 'How do I get started?', icon: 'rocket' },
];

interface PopularQuestionsProps {
  onSelectQuestion: (question: string) => void;
  disabled?: boolean;
}

const styles: Record<string, CSSProperties> = {
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    marginTop: 8,
  },
  greeting: {
    margin: '0 0 12px',
    fontSize: 22,
    fontWeight: 700,
    color: '#111827',
    letterSpacing: '-0.02em',
    lineHeight: 1.25,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    textAlign: 'left',
    padding: '12px 14px',
    background: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: 12,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
    color: '#1F2937',
    fontFamily: 'inherit',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  },
  iconWrap: {
    width: 28,
    height: 28,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
};

function QuestionIcon({ type }: { type: PopularQuestion['icon'] }) {
  if (type === 'robot') {
    return (
      <span style={{ ...styles.iconWrap, background: '#FFF1EB' }}>
        {/* Orange mark on light surfaces (web chips + mobile panel) */}
        <AvatarIcon variant="orange" size={18} />
      </span>
    );
  }

  if (type === 'sparkles') {
    return (
      <span style={{ ...styles.iconWrap, background: '#FFF8EB' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M12 3l1.2 4.2L17.5 8.5 13.2 9.8 12 14l-1.2-4.2L6.5 8.5l4.3-1.3L12 3Z" fill="#F59E0B" />
          <path d="M18 13l.7 2.3L21 16l-2.3.7L18 19l-.7-2.3L15 16l2.3-.7L18 13Z" fill="#F59E0B" />
        </svg>
      </span>
    );
  }

  if (type === 'target') {
    return (
      <span style={{ ...styles.iconWrap, background: '#FEECEC' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="7" stroke="#EF4444" strokeWidth="2" />
          <circle cx="12" cy="12" r="3.5" stroke="#EF4444" strokeWidth="2" />
          <circle cx="12" cy="12" r="1.2" fill="#EF4444" />
        </svg>
      </span>
    );
  }

  return (
    <span style={{ ...styles.iconWrap, background: '#EEF4FF' }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M5 15c4-1 6-3 7-7 1 4 3 6 7 7-4 1-6 3-7 7-1-4-3-6-7-7Z"
          fill="#3B82F6"
        />
      </svg>
    </span>
  );
}

export default function PopularQuestions({
  onSelectQuestion,
  disabled,
}: PopularQuestionsProps) {
  return (
    <section style={styles.section} aria-label="Suggested questions">
      <h2 style={styles.greeting}>Hello, how can I help you?</h2>
      <ul style={styles.list}>
        {QUESTIONS.map((q) => (
          <li key={q.id}>
            <button
              type="button"
              style={{
                ...styles.item,
                opacity: disabled ? 0.55 : 1,
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
              disabled={disabled}
              onClick={() => onSelectQuestion(q.label)}
            >
              <QuestionIcon type={q.icon} />
              <span>{q.label}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
